import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
// Le logo et le nom suivent le domaine d'arrivee : ces deux pages sont servies
// par cftsign.fr, ou une invitation par courriel depose le poseur.
import { IDENTITE } from '@/shared/marque';

const MIN = 10;

/** Page de définition de mot de passe via jeton.
 *  mode='reset' : mot de passe oublié · mode='set' : invitation d'un nouveau compte. */
export default function ResetPasswordPage({ mode = 'reset' }) {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const isInvite = mode === 'set';
  const title = isInvite ? 'Définir votre mot de passe' : 'Nouveau mot de passe';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN) { setError(`Le mot de passe doit contenir au moins ${MIN} caractères.`); return; }
    if (password !== confirm) { setError('Les deux mots de passe ne correspondent pas.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset', { token, password });
      toast.success(isInvite ? 'Mot de passe défini, vous pouvez vous connecter.' : 'Mot de passe réinitialisé.');
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.detail || 'Lien invalide ou expiré.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4" data-testid="reset-page">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="text-center mb-10">
          <img src={IDENTITE.logo} alt={IDENTITE.nom} className="w-12 h-12 rounded-xl mx-auto mb-4" />
          <h1 className="text-xl font-semibold tracking-tight text-neutral-900">{title}</h1>
        </div>

        {!token ? (
          <div className="text-center space-y-4">
            <p className="text-sm text-red-600">Lien invalide : jeton manquant.</p>
            <Link to="/login" className="text-sm text-emerald-600 hover:underline">Retour à la connexion</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-md bg-red-50 border border-red-100 text-red-600 text-sm" data-testid="reset-error">
                {error}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[13px] text-neutral-600">Nouveau mot de passe</Label>
              <div className="relative">
                <Input id="password" type={showPw ? 'text' : 'password'} value={password}
                       onChange={(e) => setPassword(e.target.value)} placeholder={`Au moins ${MIN} caractères`}
                       required className="h-10 pr-10" data-testid="reset-password-input" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                        onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm" className="text-[13px] text-neutral-600">Confirmer</Label>
              <Input id="confirm" type={showPw ? 'text' : 'password'} value={confirm}
                     onChange={(e) => setConfirm(e.target.value)} placeholder="Répétez le mot de passe"
                     required className="h-10" data-testid="reset-confirm-input" />
            </div>
            <Button type="submit" className="w-full h-10" disabled={loading} data-testid="reset-submit-btn">
              {loading ? 'Validation...' : 'Valider'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
